import { Task } from '@griply/types';
import { useQuery } from '@tanstack/react-query';
import { createSubscriptionQuery } from 'shared/lib/@tanstack-query';
import { GetTaskOptions, getTasks } from 'shared/services/backend-api';
import { ID } from 'shared/types/id';
import { chunkArray } from 'shared/utils/chunk-array';

export const queryKey = 'tasks';

export const useTasksQuery = (
  uid: ID | undefined,
  options: GetTaskOptions,
  enabled = true,
  { includeDeleted = false }: { includeDeleted?: boolean } = {},
) =>
  useQuery({
    queryKey: [
      queryKey,
      uid ?? '',
      enabled.toString(),
      ...Object.entries(options)
        .filter(([, value]) => value !== undefined)
        .map(
          ([key, value]) =>
            `${key}: ${
              value instanceof Date
                ? value.getTime().toString()
                : Array.isArray(value)
                  ? value.toSorted().join(',')
                  : value?.toString()
            }`,
        ),
    ],
    select: includeDeleted
      ? undefined
      : (data: Task[] | undefined) =>
          data?.filter(({ deletedAt }) => !deletedAt),
    queryFn: (context) => {
      if (!enabled) {
        return [];
      }

      const { goals, ids, lifeAreas, ...rest } = options;
      let queries = [() => getTasks(options)];

      if (lifeAreas?.length && lifeAreas.length > 30) {
        // chunk to max-length of 30 since firebase doesn't accept more
        const chunkedLifeAreas = chunkArray(lifeAreas.toSorted(), 30);
        queries = chunkedLifeAreas.map(
          (chunked) => () =>
            getTasks({ ...rest, ids, goals, lifeAreas: chunked }),
        );
      }

      if (goals?.length && goals.length > 30) {
        // chunk to max-length of 30 since firebase doesn't accept more
        const chunkedGoals = chunkArray(goals.toSorted(), 30);
        queries = chunkedGoals.map(
          (chunked) => () =>
            getTasks({ ...rest, ids, lifeAreas, goals: chunked }),
        );
      }

      if (ids?.length && ids.length > 30) {
        // check to max-length of 30 since firebase doesn't accept more
        const chunkedIds = chunkArray(ids.toSorted(), 30);
        queries = chunkedIds.map(
          (chunked) => () =>
            getTasks({ ...rest, goals, lifeAreas, ids: chunked }),
        );
      }

      return createSubscriptionQuery(queries)(context);
    },
    enabled: !!uid && enabled,
  });
